import AnimatedTabs from '@/components/AnimatedTabs';
import Button from '@/components/Button';
import JsonViewer from '@/components/JsonViewer';
import Panel from '@/components/panel/Panel';
import { HiOutlineDocumentText, IoCodeSlashOutline } from '@/components/react-icons/main';
import Tag from '@/components/Tag';
import copyToClipboard from '@/lib/copyToClipboard';
import getDateTimeAgo from '@/lib/getDateTimeAgo';
import normalizeToArray from '@/lib/normalizeToArray';
import sendToast from '@/lib/sendToast';
import { getAppActionOutput, putAppActionResponse } from '@/redux/actions/gravityActions';
import { setModalState } from '@/redux/actions/uiActions/modalActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import memoizeSelector from '@/redux/lib/memoizeSelector';
import selectors from '@/redux/selectors/selectors';
import formatMappers from '@/screens/apps/appEditorPage/appEditorMainPanel/apiPage/lib/formatMappers.js';
import appScreenSelectors from '@/screens/apps/redux/selectors/appScreenSelectors';
import OutputViewer from '@/screens/workflows/workflowEditor/workflowEditorPanels/primaryPanels/outputPanel/OutputViewer';
import { trackEvent } from '@shared/utils/trackEvent.js';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const tabs = [
  { value: 'mapped', label: 'Mapped' },
  { value: 'raw', label: 'Raw' }
];

const formatFullDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const MetadataHeader = ({ statusId, response, rawResponse, viewMode, setViewMode }) => {
  const appId = useArgSelector(selectors.param, 'appId');
  const appVersion = useArgSelector(selectors.param, 'version');
  const actionId = useSelector((state) => appScreenSelectors.activeAppAction(state)?.id);
  const type = useSelector((state) => appScreenSelectors.activeAppAction(state)?.type);
  const isOnDemand = useSelector((state) => appScreenSelectors.activeAppAction(state)?.triggerType === 'onDemand');
  const isLongPolling = useSelector((state) => appScreenSelectors.activeAppAction(state)?.triggerType === 'longPolling');
  const isPerResult = useSelector((state) => appScreenSelectors.activeAppAction(state)?.longPolling?.enablePerResult);
  const hasResponse = !!response;
  const date = response?.meta?.response?.headers?.date || response?.meta?.createdOn;
  const fullDateTime = formatFullDateTime(date);
  const relativeDateTime = getDateTimeAgo(date);
  const resultsCount = Array.isArray(response?.data) ? response.data.length : 0;
  const hasTruncatedOutput = response?.meta?.truncatedOutput;

  const rawMappers = useSelector(memoizeSelector('activeAppActionMappers', appScreenSelectors.activeAppActionApiMappers));
  const mappers = formatMappers(rawMappers, { type: 'response' });
  const hasWebhookIdKey = mappers?.find((n) => n?.type === 'universal')?.settings?.webhookIdKey;
  const isOnDemandTrigger = type === 'trigger' && isOnDemand;
  const showOnDemandWarning = isOnDemandTrigger && !hasWebhookIdKey;

  const handleCopyCurl = () => {
    const curl = response?.meta?.curl;
    // Only escape single quotes in JSON body
    const sanitizedCurl = curl.replace(/--data '({[\s\S]*?})'/g, (match, body) => {
      return `--data '${body.replace(/'/g, "'\\''")}'`;
    });
    copyToClipboard(sanitizedCurl, { toast: true });
  };

  const saveOutputResponse = async (code) => {
    trackEvent('app-editor-output-editResponse');
    try {
      const newResponse = code ? JSON.parse(code) : rawResponse;
      if (newResponse?.success === undefined || newResponse?.data === undefined) {
        throw new Error('Response must have a success and data property.');
      }
      await putAppActionResponse({ appId, appVersion, actionId, statusId, newResponse });
      sendToast({ message: `Response updated successfully.`, type: 'success', duration: 6000 });
    } catch (e) {
      console.error(e);
      sendToast({ message: `There was a problem updating the response: ${e?.message}`, type: 'error', duration: 6000 });
    }
  };

  const handleEditResponse = () => {
    const code = JSON.stringify(rawResponse || { success: true, data: [] }, null, 2) + '\n';
    setModalState('codeEditor', {
      open: true,
      code,
      defaultCode: code,
      size: 'full',
      hideRightPane: true,
      showFunctionWrapper: false,
      title: 'Edit Raw Response',
      description: 'Edit the raw response for this action. After saving your new response will be mapped and saved, replacing the previous one.',
      mode: 'json',
      onSave: saveOutputResponse
    });
  };

  return (
    <div className="metadata-header">
      {showOnDemandWarning && (
        <Panel.Alert //
          title="Webhook ID not found"
          description="Go to Resource Extraction in the API Response Mapper to set where to find the webhook ID key. This ID is required to detect successful webhook creation and manage it."
        />
      )}
      {isLongPolling && (
        <Panel.Alert
          title={
            <span>
              For testing, we consider the <code>{`{{trigger.lastPollOn}}`}</code> value to be 2 hours ago.
            </span>
          }
        />
      )}
      {isLongPolling && isPerResult && <Panel.Alert title={'For per-result testing, we only process the first 3 results.'} />}
      <div className="metadata-row">
        <div className="metadata-group">
          {hasResponse ? (
            <>
              <Tag variant={response.success ? 'green' : 'error'}>{response.success ? 'Success' : 'Failed'}</Tag>
              <Tag>Status: {statusId || response?.meta?.response?.status || 'N/A'}</Tag>
              {resultsCount > 0 && <Tag>{resultsCount} results</Tag>}
              {hasTruncatedOutput && (
                <Tag variant="warning" title="This output has been truncated for performance reasons.">
                  Truncated
                </Tag>
              )}
            </>
          ) : (
            <Tag variant="neutral">No Results</Tag>
          )}
        </div>
        {date ? (
          <span className="metadata-label" data-title={fullDateTime}>
            Ran {relativeDateTime}
          </span>
        ) : (
          <span className="metadata-label">No execution time available</span>
        )}
      </div>
      <div className="metadata-row">
        <div className="metadata-group">
          {response?.meta?.curl && (
            <Button className="shadow-none" size="sm" icon={IoCodeSlashOutline} onClick={handleCopyCurl} disabled={!hasResponse}>
              Copy cURL
            </Button>
          )}
          <Button className="shadow-none" size="sm" icon={HiOutlineDocumentText} onClick={handleEditResponse}>
            Edit Response
          </Button>
        </div>

        <AnimatedTabs layoutKey="output-view" className="output-view-tabs" tab={viewMode} setTab={setViewMode} tabs={tabs} disabled={!hasResponse} />
      </div>
    </div>
  );
};

const AppEditorOutput = ({ statusId: propStatusId }) => {
  const [viewMode, setViewMode] = useState('mapped');
  const appId = useArgSelector(selectors.param, 'appId');
  const appVersion = useArgSelector(selectors.param, 'version');
  const actionId = useSelector((state) => appScreenSelectors.activeAppAction(state)?.id);
  const action200Output = useSelector(memoizeSelector('activeAppAction200Output', (state) => appScreenSelectors.activeAppAction200Output(state)));
  const action400Output = useSelector(memoizeSelector('activeAppAction400Output', (state) => appScreenSelectors.activeAppAction400Output(state)));
  const action500Output = useSelector(memoizeSelector('activeAppAction500Output', (state) => appScreenSelectors.activeAppAction500Output(state)));

  // Determine statusId - use prop if provided, otherwise try to get latest from outputs
  const statusId = useMemo(() => {
    if (propStatusId) {
      return propStatusId;
    }

    // Try to get latest statusId from outputs
    const allOutputs = [
      { statusId: 200, output: action200Output },
      { statusId: 400, output: action400Output },
      { statusId: 500, output: action500Output }
    ].filter((item) => item.output?.response);

    if (allOutputs.length === 0) {
      return null;
    }

    // Get the most recent output based on updatedOn
    const sorted = allOutputs.sort((a, b) => {
      const aDate = a.output?.response?.[0]?.updatedOn || a.output?.response?.updatedOn;
      const bDate = b.output?.response?.[0]?.updatedOn || b.output?.response?.updatedOn;
      return new Date(bDate || 0) - new Date(aDate || 0);
    });

    return sorted[0]?.statusId || null;
  }, [propStatusId, action200Output, action400Output, action500Output]);

  const output = useMemo(() => {
    if (!statusId) {
      return null;
    }

    if (statusId >= 200 && statusId <= 299) {
      return normalizeToArray(action200Output?.response);
    }

    if (statusId <= 499) {
      return normalizeToArray(action400Output?.response);
    }

    return normalizeToArray(action500Output?.response);
  }, [statusId, action200Output, action400Output, action500Output]);

  const response = output?.[output?.length - 1]?.data;
  const rawResponse = output?.[0]?.data;

  useEffect(() => {
    if (actionId) {
      getAppActionOutput({ appId, appVersion, actionId, allMappers: true });
    }
  }, [actionId, appId, appVersion]);

  return (
    <>
      <MetadataHeader statusId={statusId} response={response} rawResponse={rawResponse} viewMode={viewMode} setViewMode={setViewMode} />
      {!!response && (
        <div className={'slider-content'}>
          {viewMode === 'mapped' && <OutputViewer search={null} output={response} enablePagination />}
          {viewMode === 'raw' && <JsonViewer json={rawResponse} />}
        </div>
      )}
    </>
  );
};

export default AppEditorOutput;
